import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Upload, CheckCircle2, XCircle, Loader2, Image } from 'lucide-react';
import Papa from 'papaparse';

interface ParsedBox {
  box_id: string;
  box_name: string;
  description?: string;
  email: string;
  customer_name: string;
  phone: string;
  address: string;
  tracking_number?: string;
  shipment_status?: string;
  transaction_id?: string;
  created_date: string;
  dimensions?: string;
  width?: number;
  length?: number;
  height?: number;
  // Airtable-specific
  photo_url?: string;
  box_status?: string;
  box_in_storage?: string;
}

interface MigrationResult {
  success: boolean;
  batch_id: string;
  total_customers: number;
  total_boxes: number;
  successful_customers: number;
  successful_boxes: number;
  photos_uploaded?: number;
  failed_imports: Array<{ email: string; error: string; box_count: number }>;
}

type MigrationSource = 'shopify' | 'airtable';

export default function AdminMigrateBoxes() {
  const navigate = useNavigate();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedBox[]>([]);
  const [migrationSource, setMigrationSource] = useState<MigrationSource>('airtable');
  const [batchName, setBatchName] = useState('Airtable Migration - ' + new Date().toLocaleDateString());
  const [isMigrating, setIsMigrating] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSourceChange = (source: MigrationSource) => {
    setMigrationSource(source);
    setBatchName(`${source === 'airtable' ? 'Airtable' : 'Shopify'} Migration - ${new Date().toLocaleDateString()}`);
    // Reset data when source changes
    setCsvFile(null);
    setParsedData([]);
    setResult(null);
    setParseError(null);
  };

  const handleFileUpload = (file: File | null) => {
    if (!file) {
      setCsvFile(null);
      setParsedData([]);
      setParseError(null);
      return;
    }

    setCsvFile(file);
    setParseError(null);
    setResult(null);

    // Read file as text first to check for title rows
    const reader = new FileReader();
    reader.onload = (e) => {
      let csvText = e.target?.result as string;
      
      // Check if first line is a title row (Airtable exports often have "TableName-ViewName" as first line)
      // Title rows typically don't have commas or have significantly fewer columns than the header
      const lines = csvText.split('\n');
      if (lines.length > 1) {
        const firstLineCommas = (lines[0].match(/,/g) || []).length;
        const secondLineCommas = (lines[1].match(/,/g) || []).length;
        
        // If first line has significantly fewer commas than second line, it's likely a title row
        // Also check if first line doesn't contain expected column markers
        const hasExpectedColumns = lines[0].includes('Box ID') || lines[0].includes('Email') || lines[0].includes('box_id');
        
        if (!hasExpectedColumns && firstLineCommas < secondLineCommas * 0.5) {
          console.log('Detected title row, skipping:', lines[0]);
          toast.info('Detected and skipped title row in CSV');
          csvText = lines.slice(1).join('\n');
        }
      }

      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const boxes: ParsedBox[] = results.data.map((row: any) => ({
              // Common fields (handle both Shopify and Airtable column names)
              box_id: row['Box ID'] || row['box_id'] || '',
              box_name: row['Box Name'] || row['box_name'] || '',
              description: row['Description'] || row['description'] || '',
              email: (row['Email (from User)'] || row['email'] || '')?.toLowerCase().trim(),
              customer_name: row['Name (from User)'] || row['customer_name'] || '',
              phone: row['Phone (from User)'] || row['phone'] || '',
              address: row['Address'] || row['address'] || '',
              tracking_number: row['Tracking Number (from Shipments)'] || row['tracking_number'] || '',
              shipment_status: row['Status (from Shipments)'] || row['shipment_status'] || '',
              transaction_id: row['Transactions ID'] || row['transaction_id'] || '',
              created_date: row['Created Date'] || row['created_date'] || '',
              dimensions: row['Dimensions'] || row['dimensions'] || '',
              width: parseFloat(row['Width (in)'] || row['width']) || undefined,
              length: parseFloat(row['Length (in)'] || row['length']) || undefined,
              height: parseFloat(row['Height (in)'] || row['height']) || undefined,
              // Airtable-specific fields
              photo_url: row['Photo'] || row['photo_url'] || '',
              box_status: row['Box Status'] || row['box_status'] || '',
              box_in_storage: row['Box in Storage'] || row['box_in_storage'] || '',
            }));

            // Filter out rows with missing required fields
            // For Airtable, transaction_id is optional
            const validBoxes = boxes.filter(box => {
              const hasRequired = box.box_id && box.box_name && box.email && 
                box.customer_name && box.phone && box.address && box.created_date;
              
              // Shopify requires transaction_id
              if (migrationSource === 'shopify') {
                return hasRequired && box.transaction_id;
              }
              return hasRequired;
            });

            if (validBoxes.length === 0) {
              setParseError('No valid boxes found in CSV. Please check required fields.');
              setParsedData([]);
            } else {
              setParsedData(validBoxes);
              if (validBoxes.length < boxes.length) {
                toast.warning(`${boxes.length - validBoxes.length} rows skipped due to missing required fields`);
              }
            }
          } catch (error: any) {
            setParseError('Failed to parse CSV: ' + error.message);
            setParsedData([]);
          }
        },
        error: (error) => {
          setParseError('CSV parsing error: ' + error.message);
          setParsedData([]);
        }
      });
    };
    
    reader.onerror = () => {
      setParseError('Failed to read file');
      setParsedData([]);
    };
    
    reader.readAsText(file);
  };

  const startMigration = async () => {
    if (!parsedData.length) {
      toast.error('No data to migrate');
      return;
    }

    setIsMigrating(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('migrate-shopify-boxes', {
        body: {
          batch_name: batchName,
          boxes: parsedData,
          migration_source: migrationSource
        }
      });

      if (error) throw error;

      setResult(data);
      
      if (data.failed_imports?.length > 0) {
        toast.warning(
          `Migration completed with ${data.failed_imports.length} failures. ` +
          `${data.successful_customers}/${data.total_customers} customers migrated.`
        );
      } else {
        const photoMsg = data.photos_uploaded ? `, ${data.photos_uploaded} photos` : '';
        toast.success(
          `Migration successful! ${data.successful_customers} customers, ${data.successful_boxes} boxes${photoMsg} migrated.`
        );
      }
    } catch (error: any) {
      console.error('Migration error:', error);
      toast.error('Migration failed: ' + (error.message || 'Unknown error'));
    } finally {
      setIsMigrating(false);
    }
  };

  const customerCount = new Set(parsedData.map(box => box.email)).size;
  const photoCount = parsedData.filter(box => box.photo_url).length;

  // Count statuses for preview
  const statusCounts = parsedData.reduce((acc, box) => {
    const status = box.box_in_storage || box.shipment_status || 'Unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Migrate Boxes</h1>
            <p className="text-muted-foreground">Import existing customers and boxes from CSV export</p>
          </div>
        </div>

        {/* Source Selection & CSV Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Upload CSV File</CardTitle>
            <CardDescription>
              Select the migration source and upload your CSV file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Migration Source</Label>
              <Select 
                value={migrationSource} 
                onValueChange={(v) => handleSourceChange(v as MigrationSource)}
                disabled={isMigrating}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="airtable">Airtable</SelectItem>
                  <SelectItem value="shopify">Shopify</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {migrationSource === 'airtable' 
                  ? 'Supports photos, flexible status mapping, optional transaction IDs'
                  : 'Requires transaction IDs, standard Shopify export format'}
              </p>
            </div>

            <div>
              <Label htmlFor="csv-upload">CSV File</Label>
              <Input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={(e) => handleFileUpload(e.target.files?.[0] || null)}
                disabled={isMigrating}
              />
            </div>

            {parseError && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{parseError}</AlertDescription>
              </Alert>
            )}

            <div>
              <Label htmlFor="batch-name">Batch Name</Label>
              <Input
                id="batch-name"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="e.g., Airtable Initial Migration - Nov 2024"
                disabled={isMigrating}
              />
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        {parsedData.length > 0 && !result && (
          <Card>
            <CardHeader>
              <CardTitle>Preview & Summary</CardTitle>
              <CardDescription>
                Review the data before starting migration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Total Customers</CardDescription>
                    <CardTitle className="text-3xl">{customerCount}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Total Boxes</CardDescription>
                    <CardTitle className="text-3xl">{parsedData.length}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardDescription>Avg Boxes per Customer</CardDescription>
                    <CardTitle className="text-3xl">
                      {(parsedData.length / customerCount).toFixed(1)}
                    </CardTitle>
                  </CardHeader>
                </Card>
                {migrationSource === 'airtable' && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardDescription className="flex items-center gap-1">
                        <Image className="h-3 w-3" /> Photos to Import
                      </CardDescription>
                      <CardTitle className="text-3xl">{photoCount}</CardTitle>
                    </CardHeader>
                  </Card>
                )}
              </div>

              {/* Status Breakdown */}
              <div>
                <h3 className="text-sm font-medium mb-2">Status Breakdown</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <span key={status} className="text-xs px-2 py-1 bg-muted rounded">
                      {status}: {count}
                    </span>
                  ))}
                </div>
              </div>

              {/* Data Preview */}
              <div>
                <h3 className="text-sm font-medium mb-2">First 10 Rows</h3>
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Box ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        {migrationSource === 'airtable' && <TableHead>Photo</TableHead>}
                        <TableHead>Tracking</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedData.slice(0, 10).map((box, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs">{box.box_id}</TableCell>
                          <TableCell>{box.box_name}</TableCell>
                          <TableCell>{box.customer_name}</TableCell>
                          <TableCell className="text-xs">{box.email}</TableCell>
                          <TableCell>
                            <span className="text-xs px-2 py-1 bg-muted rounded">
                              {box.box_in_storage || box.shipment_status || 'N/A'}
                            </span>
                          </TableCell>
                          {migrationSource === 'airtable' && (
                            <TableCell>
                              {box.photo_url ? (
                                <span className="text-xs text-green-600">✓ Yes</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">No</span>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="font-mono text-xs">
                            {box.tracking_number || 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {parsedData.length > 10 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    ... and {parsedData.length - 10} more boxes
                  </p>
                )}
              </div>

              {/* Migration Button */}
              <Button 
                onClick={startMigration} 
                disabled={isMigrating || !batchName.trim()}
                className="w-full"
                size="lg"
              >
                {isMigrating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Migrating...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Start Migration
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {result && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {result.failed_imports.length === 0 ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    Migration Completed Successfully
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-yellow-500" />
                    Migration Completed with Warnings
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Results Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Customers Migrated</p>
                  <p className="text-2xl font-bold">
                    {result.successful_customers}/{result.total_customers}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Boxes Migrated</p>
                  <p className="text-2xl font-bold">
                    {result.successful_boxes}/{result.total_boxes}
                  </p>
                </div>
                {result.photos_uploaded !== undefined && (
                  <div>
                    <p className="text-sm text-muted-foreground">Photos Uploaded</p>
                    <p className="text-2xl font-bold">{result.photos_uploaded}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Failed Customers</p>
                  <p className="text-2xl font-bold text-destructive">
                    {result.failed_imports.length}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Batch ID</p>
                  <p className="text-xs font-mono mt-1">{result.batch_id}</p>
                </div>
              </div>

              {/* Failed Imports */}
              {result.failed_imports.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium mb-2">Failed Imports</h3>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Box Count</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.failed_imports.map((failure, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{failure.email}</TableCell>
                            <TableCell>{failure.box_count}</TableCell>
                            <TableCell className="text-sm text-destructive">
                              {failure.error}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4">
                <Button onClick={() => {
                  setCsvFile(null);
                  setParsedData([]);
                  setResult(null);
                }} variant="outline">
                  New Migration
                </Button>
                <Button onClick={() => navigate('/admin')} variant="default">
                  Back to Admin Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
